// THE design tokens file (FR-020, FR-021, FR-023; constitution VII, X; data-model §8).
// 2026-10-15 redesign: the category-standard shopping app (PRODUCT.md, Brand Commitments). Action and
// selection come from the Akaiva mark blue, deepened for contrast; errors have their own `danger` role.
// Every text pair below is tested at 4.5:1 in both themes. Change values here, never in components.

const light = {
  background: "#F2F3F5", // ground: cool light grey under white surfaces
  surface: "#FFFFFF",
  surfaceAlt: "#F7F8FA", // inset rows, chips at rest
  border: "#E2E5EA",
  text: "#14171F",
  textMuted: "#5B6270",
  primary: "#0A6E8C", // action + selection, deepened from the Akaiva mark blue a4
  primaryText: "#0A6E8C",
  primarySoft: "#E3F3F8",
  accent: "#14171F",
  onAccent: "#FFFFFF",
  success: "#1E7A45", // from mark green a3
  successSoft: "#E5F4EB",
  warning: "#8A5A00", // from mark amber a2
  warningSoft: "#FFF3D6",
  info: "#0A6E8C",
  infoSoft: "#E3F3F8",
  danger: "#B3261E", // errors only; never the brand colour
  dangerSoft: "#FCE8E6",
  // Category tints from the Akaiva mark (a2 amber, a3 green, a1 pink), deepened for contrast.
  tintAmber: "#8A5A00",
  tintAmberSoft: "#FFF3D6",
  tintGreen: "#1E7A45",
  tintGreenSoft: "#E5F4EB",
  tintPink: "#A8325A",
  tintPinkSoft: "#FCE7EE",
  focusRing: "#0A6E8C",
  crestBackground: "#14171F",
  crestText: "#FFFFFF",
  envBadge: "#FFF3D6",
  envBadgeText: "#8A5A00",
  offline: "#E3F3F8",
  offlineText: "#0A6E8C",
  badge: "#B3261E",
  badgeText: "#FFFFFF",
  scrim: "rgba(10, 12, 18, 0.5)",
};

type Palette = { readonly [K in keyof typeof light]: string };

const dark: Palette = {
  background: "#0F1115",
  surface: "#181B22",
  surfaceAlt: "#20242D",
  border: "#2C313B",
  text: "#ECEEF2",
  textMuted: "#A3A9B5",
  primary: "#0B7A99", // button fill, white text 4.9:1
  primaryText: "#5FD0EE",
  primarySoft: "#12313D",
  accent: "#ECEEF2",
  onAccent: "#0F1115",
  success: "#6FD69B",
  successSoft: "#16301F",
  warning: "#F5C451",
  warningSoft: "#33290F",
  info: "#7FD3EC",
  infoSoft: "#12313D",
  danger: "#FFB4AB",
  dangerSoft: "#3A1D1B",
  tintAmber: "#F5C451",
  tintAmberSoft: "#33290F",
  tintGreen: "#6FD69B",
  tintGreenSoft: "#16301F",
  tintPink: "#F48FB1",
  tintPinkSoft: "#3A1B27",
  focusRing: "#5FD0EE",
  crestBackground: "#ECEEF2",
  crestText: "#0F1115",
  envBadge: "#33290F",
  envBadgeText: "#F5C451",
  offline: "#12313D",
  offlineText: "#7FD3EC",
  badge: "#C62828",
  badgeText: "#FFFFFF",
  scrim: "rgba(0, 0, 0, 0.6)",
};

export type ColorRole = keyof typeof light;
export type ThemeName = "light" | "dark";

export const tokens = {
  color: {
    light: light as Palette,
    dark,
    /** Button text on `pr` is always white (both themes). */
    onPrimary: "#FFFFFF",
    /** Akaiva mark and category accents, same in both themes (PRD §7). */
    brand: {
      a1: "#E4577A",
      a2: "#F2A900",
      a3: "#2F9E5B",
      a4: "#1AA3C6",
      a5: "#F07A28",
    },
  },
  /** Card shadow `sh` (React Native `boxShadow` string, supported on both targets). */
  shadow: {
    light: {
      card: "0 1px 2px rgba(20,23,31,.06), 0 4px 12px rgba(20,23,31,.04)",
    },
    dark: { card: "0 1px 2px rgba(0,0,0,.4)" },
  },
  font: {
    /** One system family: Roboto on Android, the device's UI face on the web. Nothing to download. */
    family:
      'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    weight: {
      regular: "400",
      medium: "500",
      semiBold: "600",
      bold: "700",
    },
  },
  /** px. `base` 14 is the body size. */
  fontSize: {
    xxs: 11,
    xs: 12,
    sm: 13,
    base: 14,
    md: 15,
    lg: 16,
    xl: 18,
    x2l: 20,
    x3l: 24,
    x4l: 28,
    x5l: 32,
    x6l: 40,
  },
  /** Line-height multiplier; use `lineHeightFor(size)` for React Native's absolute value. */
  lineHeight: { base: 1.4 },
  /** 006: letter spacing, px (the join code is shown spaced, FR-018). */
  letterSpacing: { code: 6 },
  /** Gaps, px. */
  space: {
    xxs: 4,
    xs: 6,
    sm: 8,
    smd: 10,
    md: 12,
    lg: 16,
    xl: 20,
    x2l: 24,
    x3l: 32,
  },
  radius: {
    card: 12,
    panel: 12,
    softPanel: 10,
    button: 10,
    buttonSm: 8,
    buttonLg: 12,
    input: 10,
    pill: 99,
  },
  borderWidth: {
    card: 1,
    input: 1.5,
    focusRing: 2 /* derived: visible focus outline */,
  },
  size: {
    touchTarget: 44,
    button: 44,
    buttonSm: 36,
    buttonLg: 52,
    input: 44,
    iconButton: 44,
    chip: 26,
    tab: 38,
    headerHeightDesktop: 72,
    avatar: 40,
    badge: 20,
    icon: { sm: 16, md: 20, lg: 26, xl: 40 },
    iconStroke: 1.8,
    navUnderline: 3,
    crest: 40, // derived: avatar size
    sideNav: 248, // 006: school-admin side navigation (board SABanners)
    qrPreview: 176, // 006: on-screen QR preview
  },
  /** Web only: at or above this width the desktop chrome is used (research R7). */
  breakpoint: { desktop: 1024 },
  /** Text/background pairs used by the shell; tested at 4.5:1 or more in both themes (SC-005). */
  contrastPairs: [
    ["text", "background"],
    ["text", "surface"],
    ["textMuted", "background"],
    ["textMuted", "surface"],
    ["primaryText", "background"],
    ["primaryText", "surface"],
    ["primaryText", "primarySoft"],
    ["onAccent", "accent"],
    ["crestText", "crestBackground"],
    ["envBadgeText", "envBadge"],
    ["offlineText", "offline"],
    ["badgeText", "badge"],
    // 002 forms and messages (SC-010): field error text in prt, the inline message tones.
    ["success", "successSoft"],
    ["warning", "warningSoft"],
    ["info", "infoSoft"],
    ["danger", "dangerSoft"],
    ["danger", "surface"],
    ["primaryText", "surfaceAlt"],
    ["tintAmber", "tintAmberSoft"],
    ["tintGreen", "tintGreenSoft"],
    ["tintPink", "tintPinkSoft"],
  ] as const satisfies readonly (readonly [ColorRole, ColorRole])[],
  /**
   * Non-text pairs (borders, outlines), tested at 3:1 or more (WCAG 1.4.11): selection borders in
   * `primary`, the focus ring, and the field error border in `danger`.
   */
  nonTextContrastPairs: [
    ["primary", "surface"],
    ["focusRing", "surface"],
    ["danger", "surface"],
  ] as const satisfies readonly (readonly [ColorRole, ColorRole])[],
} as const;

export type Tokens = typeof tokens;

/** Absolute line height for a font size (React Native needs px). */
export function lineHeightFor(
  fontSize: number,
  multiplier: number = tokens.lineHeight.base,
): number {
  return Math.round(fontSize * multiplier);
}
